import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, ref } from 'vue';
import Spinner from '@/components/Spinner.vue';
import { isValidEmail } from '@/utils/validation';
import {
  CUSTOM_FIELD_OPTION,
  CUSTOM_FIELD_PREFIX,
  SCANNER_CREDENTIAL_FIELDS,
} from '@/_core/entities/scanner-credential-fields/models/constants';
import type {
  TAddScannerCredentialFieldForm,
  TNormalizedScannerCredentialField,
  TScannerCredentialFieldOption,
  TSubmitScannerCredentialField,
} from '@/_core/entities/scanner-credential-fields/models/types';

type TScannerCredentialFieldsAddFieldModalProps = {
  fields: TNormalizedScannerCredentialField[];
  isDisabled?: boolean;
  showSpinner?: boolean;
  closeModal: () => void;
  submitField: TSubmitScannerCredentialField;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ScannerCredentialFieldsAddFieldModal',
  props: {
    fields: null,
    isDisabled: { type: Boolean, default: false },
    showSpinner: { type: Boolean, default: false },
    closeModal: null,
    submitField: null
  },
  setup(__props: any) {

const props = __props as { fields: TNormalizedScannerCredentialField[], isDisabled: boolean, showSpinner: boolean, closeModal: () => void, submitField: TSubmitScannerCredentialField };



const isVisible = ref(true);

const addFieldForm = reactive<TAddScannerCredentialFieldForm>({
  fieldKey: '',
  name: '',
  value: '',
});

const fieldOptions = computed<TScannerCredentialFieldOption[]>(() => {
  const activeStandardKeys = new Set(
    props.fields
      .map((field: TNormalizedScannerCredentialField) => field.fieldType)
      .filter((fieldType: string) =>
        SCANNER_CREDENTIAL_FIELDS.some(
          (field: TScannerCredentialFieldOption) => field.fieldKey === fieldType
        )
      )
  );

  const standardOptions = SCANNER_CREDENTIAL_FIELDS.filter(
    (field: TScannerCredentialFieldOption) => !activeStandardKeys.has(field.fieldKey)
  );

  return [...standardOptions, CUSTOM_FIELD_OPTION];
});

const isCustomFieldSelected = computed(
  () => addFieldForm.fieldKey === CUSTOM_FIELD_OPTION.fieldKey
);

const isEmailFieldSelected = computed(() => addFieldForm.fieldKey === 'email');

const isEmailValueValid = computed(() => {
  if (!isEmailFieldSelected.value) {
    return true;
  }

  return isValidEmail(addFieldForm.value);
});

const canAddField = computed(() => {
  if (!addFieldForm.fieldKey) {
    return false;
  }

  if (!addFieldForm.value) {
    return false;
  }

  if (!isEmailValueValid.value) {
    return false;
  }

  if (!isCustomFieldSelected.value) {
    return true;
  }

  return !!addFieldForm.name;
});

function resetForm(): void {
  addFieldForm.fieldKey = '';
  addFieldForm.name = '';
  addFieldForm.value = '';
}

function closeModal(): void {
  if (!isVisible.value) {
    return;
  }

  resetForm();
  isVisible.value = false;
  props.closeModal();
}

function onBackdropClick(): void {
  if (props.showSpinner) {
    return;
  }

  closeModal();
}

function buildField(): TNormalizedScannerCredentialField | null {
  const selectedFieldKey = addFieldForm.fieldKey;
  const value = addFieldForm.value;

  if (selectedFieldKey === CUSTOM_FIELD_OPTION.fieldKey) {
    if (!addFieldForm.name) {
      return null;
    }

    const name = addFieldForm.name;

    return {
      fieldKey: generateCustomFieldKey(name),
      fieldType: CUSTOM_FIELD_OPTION.fieldKey,
      label: name,
      name,
      value,
    };
  }

  const selectedDefinition = SCANNER_CREDENTIAL_FIELDS.find(
    (field: TScannerCredentialFieldOption) => field.fieldKey === selectedFieldKey
  );

  if (!selectedDefinition) {
    return null;
  }

  return {
    fieldKey: selectedDefinition.fieldKey,
    fieldType: selectedDefinition.fieldKey,
    label: selectedDefinition.label,
    name: '',
    value,
  };
}

function generateCustomFieldKey(name: string): string {
  const encodedName = encodeURIComponent(name).slice(0, 60);
  const randomSuffix = crypto.randomUUID();

  return `${CUSTOM_FIELD_PREFIX}${encodedName}__${randomSuffix}`;
}

async function addField(): Promise<void> {
  if (!canAddField.value) {
    return;
  }

  if (props.isDisabled || props.showSpinner) {
    return;
  }

  const nextField = buildField();

  if (!nextField) {
    return;
  }

  const isAdded = await props.submitField(nextField);

  if (!isAdded) {
    return;
  }

  closeModal();
}

return { __sfc: true,props, isVisible, addFieldForm, fieldOptions, isCustomFieldSelected, isEmailFieldSelected, isEmailValueValid, canAddField, resetForm, closeModal, onBackdropClick, buildField, generateCustomFieldKey, addField, Spinner }
}

})